import {
  MentionItem,
  BasicCurrencyResponse,
  ERC20TokenResponse,
  MentionSearchResult,
} from "../types/mention.types";

class MentionApiClient {
  private baseUrl = "https://api-swap.synk.ws";
  private debounceTimeout: NodeJS.Timeout | null = null;
  private abortController: AbortController | null = null;
  private requestId = 0;

  async searchCurrencies(
    query: string,
    onResults: (results: MentionSearchResult) => void,
    onError: (error: string) => void,
    onLoading: (loading: boolean) => void,
    searchBasic: boolean = true,
    searchERC20: boolean = true
  ): Promise<void> {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    onLoading(true);

    this.abortController = new AbortController();
    const currentRequestId = ++this.requestId;

    this.debounceTimeout = setTimeout(async () => {
      try {
        const results = await this.performSearch(
          query,
          this.abortController!.signal,
          searchBasic,
          searchERC20
        );

        if (currentRequestId === this.requestId) {
          onResults(results);
          onLoading(false);
        }
      } catch (error) {
        if (currentRequestId === this.requestId) {
          if (error instanceof Error && error.name === "AbortError") {
            return;
          }
          console.error("Mention API error:", error);
          onError(error instanceof Error ? error.message : "Network error");
          onLoading(false);
        }
      }
    }, 200);
  }

  private async performSearch(
    query: string,
    signal: AbortSignal,
    searchBasic: boolean = true,
    searchERC20: boolean = true
  ): Promise<MentionSearchResult> {
    const searchParam = query.trim() || "";

    const promises: Promise<any>[] = [];
    const promiseTypes: string[] = [];

    if (searchBasic) {
      promises.push(this.fetchBasicCurrencies(searchParam, signal));
      promiseTypes.push("basic");
    }

    if (searchERC20) {
      promises.push(this.fetchERC20Tokens(searchParam, signal));
      promiseTypes.push("erc20");
    }

    const responses = await Promise.all(promises);

    let basicItems: MentionItem[] = [];
    let erc20Items: MentionItem[] = [];

    responses.forEach((response, index) => {
      const type = promiseTypes[index];
      if (type === "basic") {
        basicItems = this.convertBasicToMentionItems(response);
      } else if (type === "erc20") {
        erc20Items = this.convertERC20ToMentionItems(response);
      }
    });

    const merged = this.mergeAndDeduplicate(basicItems, erc20Items);

    return {
      basic: basicItems,
      erc20: erc20Items,
      merged: merged.slice(0, 12),
    };
  }

  private async fetchBasicCurrencies(
    query: string,
    signal: AbortSignal
  ): Promise<BasicCurrencyResponse> {
    const url = `${
      this.baseUrl
    }/api/v1/currencies/?limit=25&offset=0&search=${encodeURIComponent(query)}`;

    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Basic currencies API error: ${response.status}`);
    }

    return response.json();
  }

  private async fetchERC20Tokens(
    query: string,
    signal: AbortSignal
  ): Promise<ERC20TokenResponse> {
    const url = `${
      this.baseUrl
    }/api/v1/currencies/erc20/?limit=25&offset=0&search=${encodeURIComponent(
      query
    )}`;

    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`ERC20 tokens API error: ${response.status}`);
    }

    return response.json();
  }

  private convertBasicToMentionItems(
    response: BasicCurrencyResponse
  ): MentionItem[] {
    const items = response.results || [];

    return items.map((result) => {
      const networkCode =
        result.currency_network_obj?.slug || result.network_name || "unknown";
      const networkName =
        result.currency_network_obj?.name ||
        result.network_name ||
        "Unknown Network";

      let icon = "";
      if (result.icon?.startsWith("http")) {
        icon = result.icon;
      } else if (result.url_icon?.startsWith("/media/")) {
        icon = `${this.baseUrl}${result.url_icon}`;
      } else {
        icon = result.icon || "";
      }

      return {
        source: "basic" as const,
        id: String(result.id),
        label: result.code,
        name: result.code_name,
        networkCode,
        networkName,
        icon,
        textValue: `${result.code} ${result.code_name} ${networkCode} ${networkName} ${result.id}`,
      };
    });
  }

  private convertBasicTopTokensToMentionItems(
    response: BasicCurrencyResponse
  ): MentionItem[] {
    const items = response.top_tokens || [];

    return items.map((result) => {
      const networkCode =
        result.currency_network_obj?.slug || result.network_name || "unknown";
      const networkName =
        result.currency_network_obj?.name ||
        result.network_name ||
        "Unknown Network";

      let icon = "";
      if (result.icon?.startsWith("http")) {
        icon = result.icon;
      } else if (result.url_icon?.startsWith("/media/")) {
        icon = `${this.baseUrl}${result.url_icon}`;
      } else {
        icon = result.icon || "";
      }

      return {
        source: "basic" as const,
        id: String(result.id),
        label: result.code,
        name: result.code_name,
        networkCode,
        networkName,
        icon,
        textValue: `${result.code} ${result.code_name} ${networkCode} ${networkName} ${result.id}`,
      };
    });
  }

  private convertERC20ToMentionItems(
    response: ERC20TokenResponse
  ): MentionItem[] {
    const items = response.tokens || [];

    return items
      .filter(
        (token) =>
          token.address?.startsWith("0x") && token.address.length === 42
      )
      .map((token) => {
        let networkCode = "unknown";
        let networkName = "Unknown Network";

        if (token.chainId === 1) {
          networkCode = "ETH";
          networkName = "Ethereum";
        } else if (token.chainId) {
          networkCode = `chainId:${token.chainId}`;
          networkName = `EVM (${token.chainId})`;
        } else if (token.symbol === "ETH") {
          networkCode = "ETH";
          networkName = "Ethereum";
        } else {
          networkCode = "EVM";
          networkName = "EVM Generic";
        }

        return {
          source: "erc20" as const,
          id: token.address,
          label: token.symbol,
          name: token.name,
          networkCode,
          networkName,
          icon: token.logoURI || "",
          textValue: `${token.symbol} ${token.name} ${networkCode} ${networkName} ${token.address}`,
        };
      });
  }

  private convertERC20TopTokensToMentionItems(
    response: ERC20TokenResponse
  ): MentionItem[] {
    const items = response.top_tokens || [];

    return items
      .filter(
        (token) =>
          token.address?.startsWith("0x") && token.address.length === 42
      )
      .map((token) => {
        let networkCode = "unknown";
        let networkName = "Unknown Network";

        if (token.chainId === 1) {
          networkCode = "ETH";
          networkName = "Ethereum";
        } else if (token.chainId) {
          networkCode = `chainId:${token.chainId}`;
          networkName = `EVM (${token.chainId})`;
        } else if (token.symbol === "ETH") {
          networkCode = "ETH";
          networkName = "Ethereum";
        } else {
          networkCode = "EVM";
          networkName = "EVM Generic";
        }

        return {
          source: "erc20" as const,
          id: token.address,
          label: token.symbol,
          name: token.name,
          networkCode,
          networkName,
          icon: token.logoURI || "",
          textValue: `${token.symbol} ${token.name} ${networkCode} ${networkName} ${token.address}`,
        };
      });
  }

  private mergeAndDeduplicate(
    basicItems: MentionItem[],
    erc20Items: MentionItem[]
  ): MentionItem[] {
    const merged = [...basicItems, ...erc20Items];

    const seen = new Set<string>();
    const deduplicated = merged.filter((item) => {
      const key = `${item.source}:${item.id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    return deduplicated;
  }

  async getTopTokens(
    onResults: (results: MentionSearchResult) => void,
    onError: (error: string) => void,
    onLoading: (loading: boolean) => void,
    tab: "all" | "basic" | "erc20" = "all"
  ): Promise<void> {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    onLoading(true);

    this.abortController = new AbortController();
    const currentRequestId = ++this.requestId;

    try {
      const shouldFetchBasic = tab === "all" || tab === "basic";
      const shouldFetchERC20 = tab === "all" || tab === "erc20";

      const promises: Promise<any>[] = [];
      const promiseTypes: string[] = [];

      if (shouldFetchBasic) {
        promises.push(
          this.fetchBasicCurrencies("", this.abortController.signal)
        );
        promiseTypes.push("basic");
      }

      if (shouldFetchERC20) {
        promises.push(this.fetchERC20Tokens("", this.abortController.signal));
        promiseTypes.push("erc20");
      }

      const responses = await Promise.all(promises);

      if (currentRequestId === this.requestId) {
        let basicTopTokens: MentionItem[] = [];
        let erc20TopTokens: MentionItem[] = [];

        responses.forEach((response, index) => {
          const type = promiseTypes[index];
          if (type === "basic") {
            basicTopTokens = this.convertBasicTopTokensToMentionItems(response);
            console.log("Basic top tokens loaded:", basicTopTokens.length);
          } else if (type === "erc20") {
            erc20TopTokens = this.convertERC20TopTokensToMentionItems(response);
            console.log("ERC20 top tokens loaded:", erc20TopTokens.length);
          }
        });

        let mergedTopTokens: MentionItem[] = [];
        if (tab === "all") {
          mergedTopTokens = [...basicTopTokens, ...erc20TopTokens];
          console.log(
            "All tab - merged top tokens:",
            mergedTopTokens.length,
            "basic:",
            basicTopTokens.length,
            "erc20:",
            erc20TopTokens.length
          );
        } else if (tab === "basic") {
          mergedTopTokens = basicTopTokens;
        } else if (tab === "erc20") {
          mergedTopTokens = erc20TopTokens;
        }

        const results: MentionSearchResult = {
          basic: basicTopTokens,
          erc20: erc20TopTokens,
          merged: mergedTopTokens,
        };

        onResults(results);
        onLoading(false);
      }
    } catch (error) {
      if (currentRequestId === this.requestId) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        console.error("Top tokens API error:", error);
        onError(error instanceof Error ? error.message : "Network error");
        onLoading(false);
      }
    }
  }

  cancelPendingRequests(): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

export const mentionApiClient = new MentionApiClient();
export default mentionApiClient;
